import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, MessageCircle, Inbox } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import ShareAppCard from "@/components/ShareAppCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RiskBadge({ level, score }) {
  const map = {
    green: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", label: "Low risk" },
    yellow: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "Moderate" },
    red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "High risk" },
  };
  const s = map[level] || map.green;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.text.replace("text-", "bg-")}`}></span>
      {s.label} · {score?.toFixed(1)}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function HistoryPage() {
  const { authHeaders, isAuthenticated } = useAuth();
  const [items, setItems] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}/user/history`, { headers: authHeaders() });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
      toast.error("Failed to load history");
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const deleteOne = async (id) => {
    try {
      await axios.delete(`${API}/user/history/${id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete all history? This can't be undone.")) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/user/history`, { headers: authHeaders() });
      setItems([]);
      toast.success("History cleared");
    } catch (e) {
      toast.error("Failed to clear");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]" data-testid="history-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1F3A5F]">Your History</h1>
            <p className="text-xs text-[#6B7280]">Past message analyses, kept private to you.</p>
          </div>
          {items && items.length > 0 && isAuthenticated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={clearing}
              data-testid="history-clear-all-btn"
              className="text-[#E07A7A] hover:bg-red-50"
            >
              {clearing ? <Loader2 className="animate-spin" size={16} /> : "Clear all"}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {!isAuthenticated && (
          <div
            className="bg-white rounded-2xl p-5 text-center border border-[#E5E7EB] shadow-soft"
            data-testid="history-guest-card"
          >
            <div className="w-14 h-14 bg-[#EAF6F4] rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Inbox size={24} className="text-[#4FB3A5]" />
            </div>
            <h3 className="text-base font-bold text-[#1F3A5F] mb-1">Save your message history</h3>
            <p className="text-sm text-[#6B7280] mb-4 leading-relaxed">
              Create a free account and every message you analyze will be saved here for later review.
            </p>
            <Link to="/signup" data-testid="history-guest-signup">
              <Button className="w-full rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-semibold">
                Create free account
              </Button>
            </Link>
            <Link to="/login" className="inline-block text-xs text-[#2F5BFF] hover:underline mt-3">
              Already have an account? Log in
            </Link>
          </div>
        )}

        {!isAuthenticated && (
          <ShareAppCard variant="compact" source="history_guest" />
        )}

        {isAuthenticated && items === null && (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-[#2F5BFF]" size={28} />
          </div>
        )}

        {isAuthenticated && items && items.length === 0 && (
          <div className="text-center py-20" data-testid="history-empty">
            <div className="w-16 h-16 bg-[#EAF6F4] rounded-full flex items-center justify-center mx-auto mb-4">
              <Inbox size={28} className="text-[#4FB3A5]" />
            </div>
            <h3 className="text-lg font-semibold text-[#1F3A5F] mb-2">No history yet</h3>
            <p className="text-sm text-[#6B7280] mb-6 max-w-xs mx-auto">
              Analyzed messages will appear here so you can review and learn.
            </p>
            <Link to="/app">
              <Button
                className="rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white"
                data-testid="history-go-home-btn"
              >
                Analyze a message
              </Button>
            </Link>
            <p className="text-xs text-[#A0A7B4] mt-6">
              Free: 3 checks/day · PRO users get unlimited ·{" "}
              <Link to="/pricing" className="text-[#2F5BFF] font-medium hover:underline" data-testid="history-empty-pro-link">
                Learn more
              </Link>
            </p>
          </div>
        )}

        {isAuthenticated && items && items.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-2xl p-4 shadow-soft border border-[#E5E7EB]"
            data-testid={`history-item-${item.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-xs text-[#6B7280]">
                <MessageCircle size={14} className="text-[#4FB3A5]" />
                <span className="capitalize">{item.relationship_type}</span>
                <span>·</span>
                <span>{formatDate(item.created_at)}</span>
              </div>
              <button
                onClick={() => deleteOne(item.id)}
                data-testid={`history-delete-${item.id}`}
                className="text-[#A0A7B4] hover:text-[#E07A7A] p-1"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <p className="text-sm text-[#2C2C2C] line-clamp-3 mb-3">{item.original_text}</p>

            <div className="flex items-center justify-between">
              <RiskBadge level={item.risk_level} score={item.risk_score} />
              {item.short_reason && (
                <span className="text-xs text-[#6B7280] truncate ml-2 max-w-[50%]">{item.short_reason}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
